import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, FilterIcon } from 'lucide-react';
interface Column<T> {
  header: string;
  accessor: keyof T | ((data: T) => React.ReactNode);
  sortable?: boolean;
  className?: string;
}
interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  pagination?: boolean;
  actions?: (item: T) => React.ReactNode;
  onRowClick?: (item: T) => void;
  className?: string;
}
function Table<T>({
  columns,
  data,
  keyField,
  pagination = true,
  actions,
  onRowClick,
  className = ''
}: TableProps<T>) {
  const [sortField, setSortField] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const handleSort = (accessor: keyof T) => {
    if (sortField === accessor) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(accessor);
      setSortDirection('asc');
    }
  };
  const sortedData = useMemo(() => {
    if (!sortField) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      const comparison = String(aValue).localeCompare(String(bValue));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortField, sortDirection]);
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, pagination]);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const getCellValue = (item: T, column: Column<T>) => {
    if (typeof column.accessor === 'function') {
      return column.accessor(item);
    }
    return item[column.accessor] as React.ReactNode;
  };
  return <div className={`overflow-x-auto ${className}`} data-id="element-592">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-593">
        <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-594">
          <tr data-id="element-595">
            {columns.map((column, index) => <th key={index} scope="col" className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${column.className || ''}`} data-id="element-596">
                <div className="flex items-center space-x-1" data-id="element-597">
                  <span data-id="element-598">{column.header}</span>
                  {column.sortable && typeof column.accessor !== 'function' && <button onClick={() => handleSort(column.accessor as keyof T)} className="focus:outline-none" data-id="element-599">
                      {sortField === column.accessor ? sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" data-id="element-600" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-601" /> : <FilterIcon className="h-4 w-4 text-gray-300 dark:text-gray-600" data-id="element-602" />}
                    </button>}
                </div>
              </th>)}
            {actions && <th scope="col" className="px-6 py-3 text-right" data-id="element-603">
                Actions
              </th>}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-604">
          {paginatedData.length > 0 ? paginatedData.map(item => <tr key={String(item[keyField])} className={onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : ''} onClick={onRowClick ? () => onRowClick(item) : undefined} data-id="element-605">
                {columns.map((column, colIndex) => <td key={colIndex} className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 ${column.className || ''}`} data-id="element-606">
                    {getCellValue(item, column)}
                  </td>)}
                {actions && <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-607">
                    {actions(item)}
                  </td>}
              </tr>) : <tr data-id="element-608">
              <td colSpan={columns.length + (actions ? 1 : 0)} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-609">
                No data available
              </td>
            </tr>}
        </tbody>
      </table>
      {pagination && totalPages > 1 && <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6" data-id="element-610">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-611">
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${currentPage === 1 ? 'text-gray-300 dark:text-gray-600 bg-gray-50 dark:bg-gray-800' : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-612">
              Previous
            </button>
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${currentPage === totalPages ? 'text-gray-300 dark:text-gray-600 bg-gray-50 dark:bg-gray-800' : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-613">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-614">
            <div data-id="element-615">
              <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-616">
                Showing{' '}
                <span className="font-medium" data-id="element-617">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium" data-id="element-618">
                  {Math.min(currentPage * itemsPerPage, sortedData.length)}
                </span>{' '}
                of <span className="font-medium" data-id="element-619">{sortedData.length}</span>{' '}
                results
              </p>
            </div>
            <div data-id="element-620">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-621">
                <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium ${currentPage === 1 ? 'text-gray-300 dark:text-gray-600 bg-gray-50 dark:bg-gray-800' : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-622">
                  <span className="sr-only" data-id="element-623">Previous</span>
                  <ChevronDownIcon className="h-5 w-5 rotate-90" data-id="element-624" />
                </button>
                {Array.from({
              length: Math.min(5, totalPages)
            }).map((_, i) => {
              let pageNumber: number;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }
              return <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNumber ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-500 text-blue-600 dark:text-blue-300' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-625">
                      {pageNumber}
                    </button>;
            })}
                <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 dark:text-gray-600 bg-gray-50 dark:bg-gray-800' : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-626">
                  <span className="sr-only" data-id="element-627">Next</span>
                  <ChevronDownIcon className="h-5 w-5 -rotate-90" data-id="element-628" />
                </button>
              </nav>
            </div>
          </div>
        </div>}
    </div>;
}
export default Table;